/**
 * pages/Settings.tsx — the preferences that belong to a person and a device, rather than to a job or a
 * business unit.
 *
 * **The screen exists because Install had nowhere to live.** A browser will not install an application
 * that never asks, and the ask cannot sit in the app bar (it is meaningless on most visits and absent on
 * iOS), nor in the account menu (which is identity and a way out, and already carries a form control it
 * had to be re-argued as a popover to justify — see `AccountMenu.tsx`). One screen, three panels, and the
 * install is the single call to action on a page otherwise made of preferences.
 *
 * **The theme control here and the quick toggle in the account menu are the same state, not two.** Both
 * are handed `themeChoice` and `onChooseTheme` by `App`, which holds the React state; `lib/theme.ts` owns
 * the storage key, the `data-theme` attribute and the three-valued choice. This module therefore writes to
 * `localStorage` nowhere and must never start: a second key is precisely the defect `lib/theme.ts` was
 * created to close, when the key was declared twice as unlinked string literals and changing one silently
 * broke the other. The two controls cannot disagree because neither of them owns anything.
 *
 * **Install has four states and each is a different sentence**, which is the whole reason it is not a
 * button with a `disabled` attribute:
 *
 *   - **already installed** — a statement of fact, with nothing to operate.
 *   - **installable (Chromium)** — the deferred prompt is in hand; press it.
 *   - **iOS Safari** — there is no API, so the words are the feature: Share, then *Add to Home Screen*.
 *   - **anything else** — the browser has not offered, and its own menu is where the answer is.
 *
 * A disabled Install button would collapse the last three into one control that does nothing and explains
 * nothing, which is `03-components.md`'s objection to a disabled control standing in for a refusal.
 *
 * **No new primitive and no new class.** Everything below is `Page`, `Card`, `Button`, `Banner`, `Badge`
 * and `RadioGroup` from the kit, plus `.row`, `.mono`, `.field__label` and `.field__hint` from the
 * stylesheet — the touch floors (44px targets, 16px controls on a coarse pointer), the focus ring and the
 * reduced-motion rules all arrive with them. The page adds no fixed furniture of its own, so the shell's safe-area insets
 * are already counted exactly once; the only place a phone is treated differently is the width of the one
 * call to action, which follows `.page__actions`' own phone rule rather than shrinking a desktop layout.
 */
import { useState } from "react";
import {
  isIOS,
  isInstalled,
  promptInstall,
  useInstallAvailable,
  type InstallOutcome,
} from "../lib/install";
import { THEME_CHOICES, type ThemeChoice } from "../lib/theme";
import { VERSION } from "../version";
import {
  Badge,
  Banner,
  Button,
  Card,
  Page,
  RadioGroup,
  useIsMobile,
  type RadioOption,
} from "../components/ui";

/**
 * The same three words the account menu uses, for the same three states.
 *
 * Deliberately not paraphrased: "Match my device" and "System" on two surfaces of one application are two
 * names for one setting, and a reader who meets both has to work out that they agree. Should a third
 * surface ever need them, hoist these into `lib/theme.ts` beside `THEME_CHOICES` rather than typing them
 * a third time — two copies is the most this can be allowed to reach.
 */
const THEME_LABEL: Readonly<Record<ThemeChoice, string>> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

/**
 * What each choice actually does, which the label cannot say.
 *
 * The third one is the point of the whole control: "System" is the ABSENCE of a stored preference, so it
 * keeps following the device afterwards rather than freezing whatever the device happened to be when it
 * was picked. That distinction is invisible until the sun goes down.
 */
const THEME_HINT: Readonly<Record<ThemeChoice, string>> = {
  light: "Always the light palette, whatever this device is set to.",
  dark: "Always the dark palette, whatever this device is set to.",
  system: "Follow this device's own setting, and keep following it when it changes.",
};

const THEME_OPTIONS: readonly RadioOption[] = THEME_CHOICES.map((choice) => ({
  value: choice,
  label: THEME_LABEL[choice],
  hint: THEME_HINT[choice],
}));

/* ---------------------------------------------------------------------------------------------------
 * Install
 * ------------------------------------------------------------------------------------------------- */

function InstallPanel(): JSX.Element {
  const available = useInstallAvailable();
  const installed = isInstalled();
  const fullWidth = useIsMobile();
  const [outcome, setOutcome] = useState<InstallOutcome | null>(null);

  if (installed) {
    return (
      <Card
        title="Install"
        support="Ticketless is installed on this device. It opens in a window of its own, from the home screen or the app list, and signs in with the same account."
      >
        <p className="row">
          <Badge tone="success">Installed</Badge>
        </p>
      </Card>
    );
  }

  if (outcome === "accepted") {
    /**
     * ⚠️ **`isInstalled()` is still false here, and correctly so.** It reads the display mode of THIS
     * window, and the window the user just installed is a different one — this tab remains an ordinary
     * browser tab for the whole of its life. Re-deriving the panel's state from it a second after the
     * install would answer "your browser has not offered to install this app" to somebody who installed
     * it while reading the sentence, so the outcome of the press is remembered instead of inferred.
     */
    return (
      <Card
        title="Install"
        support="Installed. Open Ticketless from your home screen or app list — this tab stays an ordinary browser tab, which is why nothing here looks any different."
      >
        <p className="row">
          <Badge tone="success">Installed</Badge>
        </p>
      </Card>
    );
  }

  const action = ((): JSX.Element => {
    if (available) {
      return (
        <div className="row">
          {/* The one call to action on a page of preferences. Full width on a phone, where a lone action
              sharing a line with nothing reads as an afterthought — the rule `.page__actions` already
              follows below --bp-md, applied here by hand because this button is inside a card. */}
          <Button
            variant="primary"
            block={fullWidth}
            onClick={() => {
              void promptInstall().then(setOutcome);
            }}
          >
            Install Ticketless
          </Button>
        </div>
      );
    }

    if (isIOS()) {
      return (
        <Banner tone="info">
          On an iPhone or iPad: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
          Safari offers no way for a page to do this for you, so this is the whole of it.
        </Banner>
      );
    }

    return (
      <Banner tone="info">
        This browser has not offered to install Ticketless. Most desktop browsers keep{" "}
        <strong>Install</strong> or <strong>Add to Home Screen</strong> in their own menu, beside the
        address bar — and some only offer it once a site has been visited more than once.
      </Banner>
    );
  })();

  return (
    <Card
      title="Install Ticketless"
      support="Install it as an app: a window of its own, an icon beside everything else on the device, and no address bar in the way of the list."
    >
      {action}
      {/* A dismissed prompt is reported and then left alone. Re-offering it immediately is nagging, and
          the guidance above already says where the browser keeps its own version of the offer. */}
      {outcome === "dismissed" ? <p className="field__hint">Nothing was installed.</p> : null}
    </Card>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

export interface SettingsProps {
  readonly themeChoice: ThemeChoice;
  readonly onChooseTheme: (choice: ThemeChoice) => void;
}

export function Settings({ themeChoice, onChooseTheme }: SettingsProps): JSX.Element {
  const installed = isInstalled();

  return (
    <Page
      title="Settings"
      measure="reading"
      lede="Preferences for this browser on this device. Nothing here is shared with anybody else, and nothing here belongs to a business unit — which is why this screen still opens before one is chosen."
    >
      <InstallPanel />

      <Card
        title="Appearance"
        support="The theme applies to this browser on this device, and is remembered here. It is not shared with anybody else and it does not follow you to another machine."
      >
        <RadioGroup
          legend="Theme"
          value={themeChoice}
          options={THEME_OPTIONS}
          onChange={(value) => {
            // The DOM hands back a `string`, and `ThemeChoice` is a union of three. Narrowed by looking
            // the value up in `THEME_CHOICES` rather than asserted with a cast: a cast would compile
            // happily on a value that never came from this control, and write it to the one attribute the
            // entire stylesheet keys off. An unrecognised value changes nothing, which is the right
            // failure — the reader's theme is left exactly as it was.
            const choice = THEME_CHOICES.find((candidate) => candidate === value);
            if (choice !== undefined) onChooseTheme(choice);
          }}
        />
      </Card>

      <Card
        title="About"
        support="What this build is and how it is running — the two things a support conversation opens by asking."
      >
        <div className="row">
          <span className="field__label">Version</span>
          <span className="mono">{VERSION}</span>
        </div>
        <div className="row">
          <span className="field__label">Running as</span>
          {/* A badge rather than a sentence, because it is a state and not a description — and `Badge`
              carries a glyph beside the word, so it is never the colour alone that says which. */}
          <Badge tone={installed ? "success" : "neutral"}>
            {installed ? "Installed app" : "Browser tab"}
          </Badge>
        </div>
      </Card>
    </Page>
  );
}
